import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../Button';
import { useCanUse } from '../../data/useEntitlement';
import s from './billing.module.css';

// The courtesy half of the paywall.
//
// App.jsx wraps every signed-in route except /settings in this.
//
// **It is UX, not enforcement.** The authority on whether a lapsed studio may
// change anything is firestore.rules, which refuses that studio's creates and
// updates from any client — this app, the Android app, a console — by reading
// a window the server wrote onto the billing account. This component exists
// so a studio in that state sees a sentence and a button rather than a
// calendar whose every save fails. A studio that somehow reached a page
// through it would still be refused at the database.
//
// ── How it is used ─────────────────────────────────────────────────────────
//
//   <Route path="/reports" element={
//     <RequireAuth><Paywall feature="Business Reports"><ReportsPage /></Paywall></RequireAuth>
//   } />
//
// A wrapper rather than a screen rendered instead of the feature. The decision
// it wraps is `useCanUse()`, which is `canUse` off the verdict and nothing else
// — there is no second opinion about entitlement in the client.
//
// /settings is deliberately not wrapped: Membership lives there, and it is
// where the button below goes.
//
// ── The three-way answer ───────────────────────────────────────────────────
//
// `blocked` is not `!allowed`. A studio whose billing record has not been read
// yet — a cold load, a slow snapshot, an account document `ensureAccount` has
// not stamped — is neither, and this renders the feature for it. Locking on "we
// do not know" would take the calendar away from a paying teacher whenever
// their connection was slow, a far worse failure than a lapsed studio seeing a
// screen for two seconds. The server is the authority on anything that costs
// money (`undecided` in data/useEntitlement.jsx).
//
// The fold fails open for the same case on its own account — a studio with no
// billing record is `unknown`, which is on ENTITLED_STATES — so this gate and
// the domain agree without either relying on the other.

export default function Paywall({ children, feature = 'This' }) {
  const { blocked } = useCanUse();

  if (!blocked) return children;

  return (
    <div className={s.locked}>
      <div className={s.lockedTitle}>{feature} needs an active subscription</div>
      <p className={s.lockedText}>
        Your studio&rsquo;s access has ended. Your students, lessons and invoices are
        all still here — add a payment method and everything comes straight back.
      </p>
      {/* A link styled as the primary action, rather than a button that
          navigates: the destination is a route, and a route belongs in the
          markup where a middle-click and a bookmark still work. */}
      <Link to="/settings#membership">
        <Button variant="primary">Go to Membership</Button>
      </Link>
    </div>
  );
}
